/**
 * API Service (Create React App) - SecondAid
 *
 * 1) Create `.env` at project root
 * 2) Set: REACT_APP_API_URL=https://YOUR-BACKEND.onrender.com
 * 3) Restart `npm start`
 */
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

async function handleJsonResponse(res) {
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    // ignore
  }

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data;
}

// ----------------------- Helpers (Auth header) -----------------------
function authHeaders(token) {
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

// =====================================================================
// HEALTH
// =====================================================================
export async function ping() {
  const res = await fetch(`${API_URL}/`);
  return handleJsonResponse(res);
}

// =====================================================================
// AUTH
// =====================================================================
export async function register({
  username,
  email,
  password,
  role,
  contact,
  address,
}) {
  const res = await fetch(`${API_URL}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, email, password, role, contact, address }),
  });
  return handleJsonResponse(res);
}

export async function login({ email, password }) {
  const res = await fetch(`${API_URL}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return handleJsonResponse(res); // { token }
}

export async function getMe(token) {
  const res = await fetch(`${API_URL}/me`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

// =====================================================================
// CATEGORIES
// =====================================================================
export async function getCategories() {
  const res = await fetch(`${API_URL}/categories`);
  return handleJsonResponse(res);
}

// =====================================================================
// PROMO CODES
// =====================================================================
export async function getPromoCodes() {
  const res = await fetch(`${API_URL}/promocodes`);
  return handleJsonResponse(res);
}

// =====================================================================
// PRODUCTS (items table via /products routes)
// =====================================================================
export async function getProducts() {
  const res = await fetch(`${API_URL}/products`);
  return handleJsonResponse(res);
}

export async function getProductById(id) {
  const res = await fetch(`${API_URL}/products/${id}`);
  return handleJsonResponse(res);
}

export async function addProduct(token, payload) {
  const isFormData = payload instanceof FormData;

  const res = await fetch(`${API_URL}/products`, {
    method: "POST",
    headers: {
      ...authHeaders(token),
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
    },
    body: isFormData ? payload : JSON.stringify(payload),
  });

  return handleJsonResponse(res);
}

export async function updateProduct(token, id, payload) {
  const isFormData = payload instanceof FormData;

  const res = await fetch(`${API_URL}/products/${id}`, {
    method: "PUT",
    headers: {
      ...authHeaders(token),
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
    },
    body: isFormData ? payload : JSON.stringify(payload),
  });

  return handleJsonResponse(res);
}

export async function deleteProduct(token, id) {
  const res = await fetch(`${API_URL}/products/${id}`, {
    method: "DELETE",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}
// =====================================================================
// ORDERS
// =====================================================================

export async function checkout(token, cartOrItemIds) {
  // Old style: [1,2,3]
  if (
    Array.isArray(cartOrItemIds) &&
    cartOrItemIds.length > 0 &&
    cartOrItemIds.every((x) => Number.isFinite(Number(x)))
  ) {
    const body = { item_ids: cartOrItemIds.map((x) => Number(x)) };

    const res = await fetch(`${API_URL}/orders/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(body),
    });
    return handleJsonResponse(res);
  }

  // New style: [{id, quantity}]
  const cart = Array.isArray(cartOrItemIds) ? cartOrItemIds : [];
  const items = cart
    .map((x) => ({
      item_id: Number(x?.id),
      quantity: Number(x?.quantity ?? 1),
    }))
    .filter(
      (x) =>
        Number.isFinite(x.item_id) &&
        Number.isFinite(x.quantity) &&
        x.quantity > 0,
    );

  const res = await fetch(`${API_URL}/orders/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({ items }),
  });

  return handleJsonResponse(res);
}

export async function getMyOrders(token) {
  const res = await fetch(`${API_URL}/my-orders`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

export async function getSellerOrders(token, status = "") {
  const qs = status ? `?status=${status}` : "";
  const res = await fetch(`${API_URL}/seller-orders${qs}`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

export async function getOrderById(token, id) {
  const res = await fetch(`${API_URL}/orders/${id}`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res); // { order, items }
}

export async function deliverOrder(token, id) {
  const res = await fetch(`${API_URL}/orders/${id}/deliver`, {
    method: "PUT",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

export async function cancelOrder(token, id) {
  const res = await fetch(`${API_URL}/orders/${id}/cancel`, {
    method: "PUT",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}
export async function shipOrder(token, id) {
  const res = await fetch(`${API_URL}/orders/${id}/ship`, {
    method: "PUT",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}
// =====================================================================
// COMMUNITY: REQUESTS + OFFERS
// =====================================================================

// Buyer creates a request
export async function createRequest(token, payload) {
  // payload: { product_category_id, title, description, max_budget, preferred_condition }
  const res = await fetch(`${API_URL}/requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res); // { id }
}

// Sellers view the board (default open) - PUBLIC
export async function getRequests(status = "open") {
  const res = await fetch(`${API_URL}/requests?status=${status}`);
  return handleJsonResponse(res);
}

// Buyer views own requests
export async function getMyRequests(token) {
  const res = await fetch(`${API_URL}/my-requests`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

// Request detail + offers
export async function getRequestById(token, id) {
  const res = await fetch(`${API_URL}/requests/${id}`, {
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res); // { request, offers }
}

// Seller replies to a request
export async function createOffer(token, requestId, payload) {
  // payload: { item_id?, offered_price?, message? }
  const res = await fetch(`${API_URL}/requests/${requestId}/offers`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res); // { id }
}

// Buyer accepts one offer
export async function acceptOffer(token, offerId) {
  const res = await fetch(`${API_URL}/offers/${offerId}/accept`, {
    method: "PUT",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}

// Seller withdraws own offer
export async function withdrawOffer(token, offerId) {
  const res = await fetch(`${API_URL}/offers/${offerId}/withdraw`, {
    method: "PUT",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}
// Buyer edits own request
export async function updateRequest(token, id, payload) {
  const res = await fetch(`${API_URL}/requests/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res);
}

// Buyer deletes own request
export async function removeRequest(token, id) {
  const res = await fetch(`${API_URL}/requests/${id}`, {
    method: "DELETE",
    headers: { ...authHeaders(token) },
  });
  return handleJsonResponse(res);
}
